"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Building2, Check, ChevronDown, PhoneOutgoing, StickyNote, UserRound, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { phoneTextareaClassGray } from "@/components/settings/phone/ui/phone-field-borders"
import { formatPhoneForDisplay } from "@/components/crm/contact/contact-edit-form"
import { CommandPage } from "@/lib/command"
import { applyContactViewResponse } from "@/lib/contact-command-data"
import { fetchContactDetailCached } from "@/lib/contact-detail-cache"
import { DIAL_DISPOSITION_GROUPS, type DialDispositionId } from "@/lib/list-dialer/dispositions"
import {
  normalizePipelineStageOptions,
  pipelineStageLabel,
  pipelineStageOptionsWithNone,
  pipelineStagePosition,
  type PipelineStageOption,
} from "@/lib/pipeline-stages"
import { cn } from "@/lib/utils"

const CONTACT_COMMAND_PATH = "/app/crm/base/cmd/contact"

const statusOptions = [
  { value: "contact", label: "Contact", dotClassName: "bg-slate-500" },
  { value: "lead", label: "Lead", dotClassName: "bg-sky-500" },
  { value: "opportunity", label: "Opportunity", dotClassName: "bg-emerald-500" },
  { value: "customer", label: "Customer", dotClassName: "bg-blue-500" },
  { value: "canceled", label: "Canceled", dotClassName: "bg-amber-500" },
  { value: "dnc", label: "Do Not Call", dotClassName: "bg-red-500" },
] as const

const CONTACT_STATUS_TO_API: Record<string, string> = {
  contact: "CONTACT",
  lead: "LEAD",
  opportunity: "OPPORTUNITY",
  customer: "CUSTOMER",
  canceled: "CANCELED",
  dnc: "DO_NOT_CALL",
}

function contactStatusToOption(contactStatus: string | undefined): string {
  if (!contactStatus) return "contact"
  const lower = contactStatus.toLowerCase()
  if (lower === "do_not_call") return "dnc"
  return lower
}

export type CallResultDialogSnapshot = {
  contactId: string | null
  contactName: string | null
  remotePhone: string | null
  callId: string | null
}

export type CallResultDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  snapshot: CallResultDialogSnapshot | null
}

export function CallResultDialog({ open, onOpenChange, snapshot }: CallResultDialogProps) {
  const contactId = snapshot?.contactId?.trim() ?? ""
  const hasContact = Boolean(contactId)

  const [disposition, setDisposition] = useState<DialDispositionId | "">("")
  const [note, setNote] = useState("")
  const [contactData, setContactData] = useState<Record<string, unknown> | null>(null)
  const [loadingContact, setLoadingContact] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [status, setStatus] = useState("contact")
  const [statusUpdatePending, setStatusUpdatePending] = useState(false)
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null)

  const [pipelineStage, setPipelineStage] = useState("")
  const [inPipeline, setInPipeline] = useState(false)
  const [pipelineUpdatePending, setPipelineUpdatePending] = useState(false)
  const [pipelineUpdateError, setPipelineUpdateError] = useState<string | null>(null)

  const [savePending, setSavePending] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const applyContactUpdate = useCallback((next: Record<string, unknown>) => {
    setContactData(next)
    setStatus(contactStatusToOption((next.contact_status as string) ?? (next.status as string)))
    const stage = String(next.pipeline_stage ?? "").trim()
    setPipelineStage(stage)
    if (next.in_pipeline === true) {
      setInPipeline(true)
    } else if (next.in_pipeline === false) {
      setInPipeline(false)
    } else {
      setInPipeline(Boolean(String(next.pipeline_opportunity_id ?? "").trim()))
    }
  }, [])

  useEffect(() => {
    if (!open) return
    setDisposition("")
    setNote("")
    setLoadError(null)
    setStatusUpdateError(null)
    setPipelineUpdateError(null)
    setSaveError(null)
    setContactData(null)

    if (!hasContact) {
      setStatus("contact")
      setPipelineStage("")
      setInPipeline(false)
      return
    }

    let cancelled = false
    setLoadingContact(true)
    void fetchContactDetailCached(contactId)
      .then((data) => {
        if (cancelled) return
        if (!data) {
          setLoadError("Could not load contact.")
          return
        }
        applyContactUpdate(data)
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : String(e))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingContact(false)
      })

    return () => {
      cancelled = true
    }
  }, [applyContactUpdate, contactId, hasContact, open])

  const pipelineStageOptions = useMemo((): PipelineStageOption[] => {
    const raw = contactData?.pipeline_stage_options
    return normalizePipelineStageOptions(Array.isArray(raw) ? raw : null)
  }, [contactData?.pipeline_stage_options])

  const pipelineStageMenuOptions = useMemo(
    () => pipelineStageOptionsWithNone(pipelineStageOptions),
    [pipelineStageOptions]
  )

  const pipelineStagePositionLabel = useMemo(() => {
    const pos = pipelineStagePosition(pipelineStage, pipelineStageOptions)
    if (!pos) return null
    return `${pos.current} of ${pos.total}`
  }, [pipelineStage, pipelineStageOptions])

  const handleDispositionToggle = useCallback((code: DialDispositionId) => {
    setDisposition((current) => (current === code ? "" : code))
  }, [])

  const handleContactStatusChange = useCallback(
    async (nextUiValue: string) => {
      if (!hasContact) return
      const apiStatus = CONTACT_STATUS_TO_API[nextUiValue]
      if (!apiStatus) {
        setStatusUpdateError("Invalid status.")
        return
      }
      if (nextUiValue === status) return

      const previousUi = status
      setStatusUpdateError(null)
      setStatus(nextUiValue)
      setStatusUpdatePending(true)
      try {
        const cmd = CommandPage(CONTACT_COMMAND_PATH)
        const res = await cmd.updateContactStatus({
          contact_id: contactId,
          contact_status: apiStatus,
        })
        if (!res.ok) {
          setStatus(previousUi)
          setStatusUpdateError(res.error ?? "Could not update status")
          return
        }
        if (!applyContactViewResponse(res.data, applyContactUpdate)) {
          setStatus(previousUi)
          setStatusUpdateError("Could not apply updated contact from server response.")
        }
      } catch (e) {
        setStatus(previousUi)
        setStatusUpdateError(e instanceof Error ? e.message : String(e))
      } finally {
        setStatusUpdatePending(false)
      }
    },
    [applyContactUpdate, contactId, hasContact, status],
  )

  const handlePipelineStageSelect = useCallback(
    async (nextStage: string) => {
      if (!hasContact) return
      const normalized = (nextStage ?? "").trim()
      if (normalized === pipelineStage) return

      setPipelineUpdateError(null)

      if (!normalized) {
        if (!inPipeline) return
        setPipelineUpdatePending(true)
        try {
          const cmd = CommandPage(CONTACT_COMMAND_PATH)
          const res = await cmd.updateContactPipeline({
            contact_id: contactId,
            action: "remove",
          })
          if (!res.ok) {
            setPipelineUpdateError(res.error ?? "Could not remove from pipeline")
            return
          }
          if (!applyContactViewResponse(res.data, applyContactUpdate)) {
            setPipelineUpdateError("Could not apply updated contact from server response.")
          }
        } catch (e) {
          setPipelineUpdateError(e instanceof Error ? e.message : String(e))
        } finally {
          setPipelineUpdatePending(false)
        }
        return
      }

      if (!inPipeline) return

      const previousStage = pipelineStage
      setPipelineStage(normalized)
      setPipelineUpdatePending(true)
      try {
        const cmd = CommandPage(CONTACT_COMMAND_PATH)
        const res = await cmd.updateContactPipeline({
          contact_id: contactId,
          action: "update",
          pipeline_stage: normalized,
        })
        if (!res.ok) {
          setPipelineStage(previousStage)
          setPipelineUpdateError(res.error ?? "Could not update pipeline stage")
          return
        }
        if (!applyContactViewResponse(res.data, applyContactUpdate)) {
          setPipelineStage(previousStage)
          setPipelineUpdateError("Could not apply updated contact from server response.")
        }
      } catch (e) {
        setPipelineStage(previousStage)
        setPipelineUpdateError(e instanceof Error ? e.message : String(e))
      } finally {
        setPipelineUpdatePending(false)
      }
    },
    [applyContactUpdate, contactId, hasContact, inPipeline, pipelineStage],
  )

  const handleSave = useCallback(async () => {
    setSaveError(null)
    setSavePending(true)
    try {
      const trimmedNote = note.trim()
      const callId = snapshot?.callId?.trim() ?? ""
      const cmd = CommandPage(CONTACT_COMMAND_PATH)

      if (hasContact && disposition) {
        const res = await cmd.recordCallDisposition({
          contact_id: contactId,
          call_id: callId || undefined,
          disposition,
          notes: trimmedNote || undefined,
        })
        if (!res.ok) {
          setSaveError(res.error ?? "Could not save call disposition")
          return
        }
        if (!applyContactViewResponse(res.data, applyContactUpdate)) {
          setSaveError("Disposition saved but contact response was invalid.")
          return
        }
      } else if (hasContact && trimmedNote) {
        const res = await cmd.createContactNote({ contact_id: contactId, body: trimmedNote })
        if (!res.ok) {
          setSaveError(res.error ?? "Could not save note")
          return
        }
        if (!applyContactViewResponse(res.data, applyContactUpdate)) {
          setSaveError("Note saved but contact response was invalid.")
          return
        }
      }

      onOpenChange(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSavePending(false)
    }
  }, [applyContactUpdate, contactId, disposition, hasContact, note, onOpenChange, snapshot?.callId])

  const contactName =
    snapshot?.contactName?.trim() ||
    (snapshot?.remotePhone?.trim() ? null : "Unknown caller")
  const phoneDisplay = snapshot?.remotePhone?.trim()
    ? formatPhoneForDisplay(snapshot.remotePhone.trim())
    : ""
  const isOrganization = Boolean(
    contactData?.is_organization === true ||
      contactData?.is_org === true ||
      (typeof contactData?.org_name === "string" && String(contactData.org_name).trim()),
  )
  const NameIcon = isOrganization ? Building2 : UserRound
  const displayName = contactName || phoneDisplay || "Unknown caller"
  const showPhoneUnderName = Boolean(contactName && phoneDisplay)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="flex max-h-[min(90vh,_52rem)] max-w-lg flex-col overflow-hidden bg-gray-100 dark:bg-gray-800"
      >
        <DialogHeader className="pb-5">
          <DialogTitle className="text-black dark:text-white">Call Result</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4 px-4 pb-4">
            <div className="flex w-1/2 items-center gap-2.5 rounded-sm border border-sky-300 bg-white px-3 py-2.5 dark:border-sky-500/50 dark:bg-slate-900/40">
              <NameIcon
                className="size-8 shrink-0 text-slate-500 dark:text-slate-400"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {displayName}
                </p>
                {showPhoneUnderName ? (
                  <p className="mt-0.5 truncate text-sm text-slate-600 dark:text-slate-300">
                    {phoneDisplay}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-black dark:text-white">
                <PhoneOutgoing className="size-4 shrink-0" />
                Call Disposition
              </p>
              <div className="flex flex-col gap-1.5">
                {DIAL_DISPOSITION_GROUPS.map((group) => (
                  <div
                    key={group.id}
                    role="group"
                    className="flex w-full flex-nowrap items-stretch gap-1.5"
                  >
                    {group.options.map(({ label, value }) => {
                      const selected = disposition === value
                      return (
                        <Button
                          key={value}
                          type="button"
                          variant="ghost"
                          size="sm"
                          title={label}
                          aria-label={label}
                          aria-pressed={selected}
                          onClick={() => handleDispositionToggle(value)}
                          className={cn(
                            "box-border h-auto min-h-8 min-w-0 flex-1 basis-0 rounded-md border-2 bg-gradient-to-b px-1.5 py-1 text-xs leading-snug font-semibold text-black shadow-none",
                            selected
                              ? "border-sky-400 from-sky-100 to-sky-50 hover:from-sky-100 hover:to-sky-50 dark:border-sky-400 dark:from-sky-900/60 dark:to-sky-950/40 dark:text-white dark:hover:from-sky-900/60 dark:hover:to-sky-950/40"
                              : "border-transparent from-slate-100 to-slate-50 shadow-[inset_0_0_0_1px_rgb(156_163_175)] hover:from-slate-200 hover:to-slate-100 dark:from-slate-700 dark:to-slate-800 dark:text-white dark:shadow-[inset_0_0_0_1px_rgb(107_114_128)] dark:hover:from-slate-600 dark:hover:to-slate-700",
                          )}
                        >
                          {label}
                        </Button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label
                htmlFor="call-result-note"
                className="flex items-center gap-1.5 text-sm font-semibold text-black dark:text-white"
              >
                <StickyNote className="size-4 shrink-0" />
                Add Note
              </Label>
              <Textarea
                id="call-result-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notes…"
                className={cn(phoneTextareaClassGray, "min-h-20 font-sans")}
              />
            </div>

            {hasContact ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-black dark:text-white">Status</p>
                  {loadingContact ? (
                    <p className="mt-1 text-xs text-black/60 dark:text-white/60">Loading…</p>
                  ) : null}
                  {loadError ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {loadError}
                    </p>
                  ) : null}
                  {statusUpdateError ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {statusUpdateError}
                    </p>
                  ) : null}
                  <div className="mt-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={statusUpdatePending || loadingContact || !contactData}
                          className="w-full min-w-0 justify-between border-gray-400 bg-white text-black dark:border-gray-500 dark:bg-card dark:text-white"
                        >
                          <span className="flex min-w-0 items-center gap-2 truncate">
                            <span
                              className={cn(
                                "size-2 shrink-0 rounded-full",
                                statusOptions.find((o) => o.value === status)?.dotClassName ??
                                  "bg-gray-400",
                              )}
                              aria-hidden
                            />
                            <span className="truncate">
                              {statusOptions.find((o) => o.value === status)?.label ?? status}
                            </span>
                          </span>
                          <ChevronDown className="size-4 shrink-0 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="w-[var(--radix-dropdown-menu-trigger-width)]"
                      >
                        {statusOptions.map(({ value, label, dotClassName }) => (
                          <DropdownMenuItem
                            key={value}
                            disabled={statusUpdatePending}
                            onClick={() => {
                              void handleContactStatusChange(value)
                            }}
                            className="flex items-center gap-2"
                          >
                            <span
                              className={cn("size-2 shrink-0 rounded-full", dotClassName)}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1">{label}</span>
                            {value === "opportunity" ? (
                              <span className="shrink-0 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                In Pipeline
                              </span>
                            ) : null}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-semibold text-black dark:text-white">
                    Pipeline Stage
                  </p>
                  {pipelineUpdateError ? (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
                      {pipelineUpdateError}
                    </p>
                  ) : null}
                  <div className="mt-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            pipelineUpdatePending ||
                            loadingContact ||
                            !contactData ||
                            !inPipeline
                          }
                          className="w-full min-w-0 justify-between border-gray-400 bg-white text-black dark:border-gray-500 dark:bg-card dark:text-white"
                        >
                          <span
                            className={cn(
                              "truncate",
                              !inPipeline && "italic text-black/70 dark:text-white/70",
                            )}
                          >
                            {inPipeline
                              ? pipelineStageLabel(pipelineStage, pipelineStageOptions)
                              : "None"}
                          </span>
                          <ChevronDown className="size-4 shrink-0 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="w-[var(--radix-dropdown-menu-trigger-width)]"
                      >
                        <DropdownMenuItem
                          disabled={pipelineUpdatePending}
                          onClick={() => {
                            void handlePipelineStageSelect("")
                          }}
                          className="italic text-black/70 dark:text-white/70"
                        >
                          None
                        </DropdownMenuItem>
                        {pipelineStageMenuOptions.filter((option) => option.value).map(
                          ({ value, label }) => (
                            <DropdownMenuItem
                              key={value}
                              disabled={pipelineUpdatePending}
                              onClick={() => {
                                void handlePipelineStageSelect(value)
                              }}
                              className="flex items-center gap-2"
                            >
                              {label}
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {inPipeline && pipelineStagePositionLabel ? (
                      <p className="mt-1 text-right text-xs tabular-nums text-slate-600 dark:text-slate-300">
                        {pipelineStagePositionLabel}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {saveError ? (
          <p className="px-4 text-sm text-red-600 dark:text-red-400" role="alert">
            {saveError}
          </p>
        ) : null}

        <DialogFooter className="mt-auto flex-shrink-0 flex-row justify-end gap-2 border-t border-gray-300 pt-4 dark:border-gray-600 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 border border-slate-500 bg-gradient-to-b from-slate-100 to-slate-200/80 text-slate-700 shadow-[0_2px_0_0_rgb(0_0_0/0.08)] dark:border-slate-500 dark:from-slate-700 dark:to-slate-600 dark:text-slate-200 dark:shadow-[0_2px_0_0_rgb(0_0_0/0.2)]"
            aria-label="Cancel"
            disabled={savePending}
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4 shrink-0" />
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 border border-sky-600 bg-gradient-to-b from-sky-50 to-sky-100/80 text-sky-800 shadow-[0_2px_0_0_rgb(0_0_0/0.08)] dark:border-sky-500 dark:from-sky-950/50 dark:to-sky-900/40 dark:text-sky-200 dark:shadow-[0_2px_0_0_rgb(0_0_0/0.2)]"
            aria-label="Save"
            disabled={savePending}
            onClick={() => void handleSave()}
          >
            <Check className="size-4 shrink-0" />
            {savePending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
